/**
 * Typography library from Figma Type System (node 5765:128817).
 * Each style stores: fontSize, lineHeight, letterSpacing, fontWeight — exactly as in Figma.
 *
 * Usage:
 *   import { textStyles, getTextStyle } from '../theme';
 *   <Typography sx={getTextStyle('title/large-700')}>Hello</Typography>
 *   // or
 *   <Box sx={textStyles['title/large-700']}>Hello</Box>
 */

export const fontFamily = {
  primary: '"Host Grotesk", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, sans-serif',
};

/** Figma weight scale */
export const fontWeight = {
  light: 200,
  semiLight: 300,
  regular: 400,
  medium: 500,
  semiBold: 600,
  bold: 700,
  black: 900,
};

/** Human-readable weight labels (matches Figma) */
export const fontWeightLabels = {
  200: 'Light',
  300: 'SemiLight',
  400: 'Regular',
  500: 'Medium',
  600: 'SemiBold',
  700: 'Bold',
  900: 'Black',
};

/**
 * Build a MUI-ready sx object from Figma specs.
 * @param {number} fontSize       - px
 * @param {number} lineHeight       - px (Figma line height value)
 * @param {number} letterSpacing    - px
 * @param {number} fontWeight       - 200–900
 */
export function defineTextStyle({
  fontSize,
  lineHeight,
  letterSpacing = 0,
  fontWeight: weight = 400,
}) {
  return {
    fontFamily: fontFamily.primary,
    fontSize: `${fontSize}px`,
    lineHeight: `${lineHeight}px`,
    letterSpacing: `${letterSpacing}px`,
    fontWeight: weight,
  };
}

/**
 * Full Figma text-style library.
 * Keys match Figma naming: category/size-weight (e.g. title/large-700).
 */
export const textStyles = {
  // Display
  'display/large': defineTextStyle({ fontSize: 60, lineHeight: 72, fontWeight: 400 }),
  'display/medium': defineTextStyle({ fontSize: 45, lineHeight: 52, fontWeight: 400 }),
  'display/small': defineTextStyle({ fontSize: 36, lineHeight: 44, fontWeight: 400 }),

  // Headline
  'headline/large': defineTextStyle({ fontSize: 32, lineHeight: 40, fontWeight: 400 }),
  'headline/medium-700': defineTextStyle({ fontSize: 28, lineHeight: 36, fontWeight: 700 }),
  'headline/medium-400': defineTextStyle({ fontSize: 28, lineHeight: 36, fontWeight: 400 }),
  'headline/small-700': defineTextStyle({ fontSize: 24, lineHeight: 32, fontWeight: 700 }),
  'headline/small-500': defineTextStyle({ fontSize: 24, lineHeight: 32, fontWeight: 500 }),
  'headline/small-400': defineTextStyle({ fontSize: 24, lineHeight: 32, fontWeight: 400 }),

  // Title
  'title/large-700': defineTextStyle({ fontSize: 20, lineHeight: 28, letterSpacing: 0, fontWeight: 700 }),
  'title/large-500': defineTextStyle({ fontSize: 20, lineHeight: 28, letterSpacing: 0, fontWeight: 500 }),
  'title/medium-700': defineTextStyle({ fontSize: 16, lineHeight: 24, letterSpacing: 0.25, fontWeight: 700 }),
  'title/medium-500': defineTextStyle({ fontSize: 16, lineHeight: 24, letterSpacing: 0.15, fontWeight: 500 }),
  'title/small-500': defineTextStyle({ fontSize: 14, lineHeight: 20, letterSpacing: 0.1, fontWeight: 500 }),

  // Label
  'label/x-large-600': defineTextStyle({ fontSize: 16, lineHeight: 22, letterSpacing: 0, fontWeight: 600 }),
  'label/large-600': defineTextStyle({ fontSize: 14, lineHeight: 20, letterSpacing: 0.2, fontWeight: 600 }),
  'label/large-500': defineTextStyle({ fontSize: 14, lineHeight: 20, letterSpacing: 0.2, fontWeight: 500 }),
  'label/medium-700': defineTextStyle({ fontSize: 12, lineHeight: 16, letterSpacing: 0.1, fontWeight: 700 }),
  'label/medium-500': defineTextStyle({ fontSize: 12, lineHeight: 16, letterSpacing: 0.25, fontWeight: 500 }),
  'label/small-500': defineTextStyle({ fontSize: 11, lineHeight: 17, letterSpacing: 0.1, fontWeight: 500 }),
  'label/small-300': defineTextStyle({ fontSize: 11, lineHeight: 17, letterSpacing: 0.34, fontWeight: 300 }),
  'label/tiny-400': defineTextStyle({ fontSize: 10, lineHeight: 15, letterSpacing: 0.2, fontWeight: 400 }),

  // Body
  'body/large-400': defineTextStyle({ fontSize: 16, lineHeight: 24, letterSpacing: 0, fontWeight: 400 }),
  'body/medium-400': defineTextStyle({ fontSize: 14, lineHeight: 20, letterSpacing: 0.15, fontWeight: 400 }),
  'body/small-400': defineTextStyle({ fontSize: 12, lineHeight: 16, letterSpacing: 0.37, fontWeight: 400 }),
};

/** camelCase aliases → Figma keys */
export const textStyleAliases = {
  displayLarge: 'display/large',
  displayMedium: 'display/medium',
  displaySmall: 'display/small',
  headlineLarge: 'headline/large',
  headlineMedium700: 'headline/medium-700',
  headlineMedium400: 'headline/medium-400',
  headlineSmall700: 'headline/small-700',
  headlineSmall500: 'headline/small-500',
  headlineSmall400: 'headline/small-400',
  titleLarge700: 'title/large-700',
  titleLarge500: 'title/large-500',
  titleMedium700: 'title/medium-700',
  titleMedium500: 'title/medium-500',
  titleSmall500: 'title/small-500',
  labelXLarge600: 'label/x-large-600',
  labelLarge600: 'label/large-600',
  labelLarge500: 'label/large-500',
  labelMedium700: 'label/medium-700',
  labelMedium500: 'label/medium-500',
  labelSmall500: 'label/small-500',
  labelSmall300: 'label/small-300',
  labelTiny400: 'label/tiny-400',
  bodyLarge400: 'body/large-400',
  bodyMedium400: 'body/medium-400',
  bodySmall400: 'body/small-400',
};

/**
 * Resolve a text style by Figma key or camelCase alias.
 * @param {string} name - e.g. 'title/large-700' or 'titleLarge700'
 * @returns {object} MUI sx typography object
 */
export function getTextStyle(name) {
  const key = textStyles[name] ? name : textStyleAliases[name];
  const style = textStyles[key];
  if (!style) {
    console.warn(`[theme] Unknown text style: "${name}"`);
    return textStyles['body/medium-400'];
  }
  return style;
}

export default textStyles;
